import React, { useCallback, useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../services/api';
import './FoodSearch.css';

function FoodSearch({ culture, userId, onSelect }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [checkingFoodId, setCheckingFoodId] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [popularFoods, setPopularFoods] = useState([]);
  const [recentSearches, setRecentSearches] = useState([]);
  const debounceTimer = useRef(null);
  const inputRef = useRef(null);

  const loadPopularFoods = useCallback(async () => {
    try {
      const foods = await api.searchFoods('', culture, userId);
      // Get first 8 foods as popular
      setPopularFoods(foods.slice(0, 8));
    } catch (error) {
      console.error('Error loading popular foods:', error);
    }
  }, [culture, userId]);

  // Load popular foods on mount and when culture/userId changes
  useEffect(() => {
    loadPopularFoods();
    loadRecentSearches();
  }, [culture, userId, loadPopularFoods]);

  const loadRecentSearches = () => {
    const recent = localStorage.getItem('foodSearchHistory');
    if (recent) {
      try {
        setRecentSearches(JSON.parse(recent).slice(0, 5));
      } catch (e) {
        console.error('Error loading recent searches:', e);
      }
    }
  };

  const saveRecentSearch = (foodName) => {
    const recent = JSON.parse(localStorage.getItem('foodSearchHistory') || '[]');
    const updated = [foodName, ...recent.filter(f => f !== foodName)].slice(0, 10);
    try {
      localStorage.setItem('foodSearchHistory', JSON.stringify(updated));
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.warn('⚠️ localStorage quota exceeded for search history, clearing old entries...');
        // Keep only the last 10 items
        const trimmed = updated.slice(-10);
        try {
          localStorage.setItem('foodSearchHistory', JSON.stringify(trimmed));
        } catch (retryError) {
          console.error('❌ Failed to save search history:', retryError);
        }
      } else {
        console.error('❌ Error saving search history:', error);
      }
    }
    setRecentSearches(updated.slice(0, 5));
  };

  useEffect(() => {
    if (query.trim().length < 2) {
      setResults([]);
      setShowSuggestions(query.length > 0);
      return;
    }

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(async () => {
      setLoading(true);
      setShowSuggestions(false);
      try {
        const foods = await api.searchFoods(query, culture, userId);
        setResults(foods);
      } catch (error) {
        console.error('Error searching foods:', error);
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 300);

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [query, culture, userId]);

  const handleSelect = async (food) => {
    if (!food) return;
    setFocused(false);
    setShowSuggestions(false);
    setCheckingFoodId(food.id || food.name);
    saveRecentSearch(food.name);
    
    try {
      const response = await api.checkFoodSafety(food.name, culture, 'en', userId);
      onSelect(response);
      if (userId) {
        window.dispatchEvent(new CustomEvent('food-history-updated', { detail: { userId: String(userId) } }));
      }
      setQuery('');
      setResults([]);
    } catch (error) {
      console.error('Error checking food from search:', error);
      onSelect({
        foodName: food.name,
        safetyStatus: food.safetyStatus || 'UNKNOWN',
        message: 'Unable to fetch detailed guidance right now.',
        reason: food.reason,
        alternative: food.alternative,
      });
      setQuery('');
      setResults([]);
    } finally {
      setCheckingFoodId(null);
    }
  };

  const handleRecentSearchClick = (searchTerm) => {
    setQuery(searchTerm);
    inputRef.current?.focus();
  };

  const getStatusConfig = (status) => {
    const configs = {
      SAFE: { icon: '✅', color: '#10B981', bg: 'rgba(16, 185, 129, 0.1)', label: 'Safe' },
      LIMITED: { icon: '⚠️', color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)', label: 'Caution' },
      UNSAFE: { icon: '❌', color: '#EF4444', bg: 'rgba(239, 68, 68, 0.1)', label: 'Unsafe' },
      CAUTION: { icon: '⚠️', color: '#F59E0B', bg: 'rgba(245, 158, 11, 0.1)', label: 'Caution' },
      UNKNOWN: { icon: '❓', color: '#6B7280', bg: 'rgba(107, 114, 128, 0.1)', label: 'Unknown' }
    };
    return configs[status] || configs.UNKNOWN;
  };

  return (
    <div className="food-search-container-modern">
      <div className="search-input-wrapper-modern">
        <div className="input-container-modern">
          <div className="search-icon-wrapper">
            <span className="search-icon">🔍</span>
          </div>
          <input
            ref={inputRef}
            type="text"
            className="search-input-field-modern"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => {
              setFocused(true);
              if (query.length < 2) {
                setShowSuggestions(true);
              }
            }}
            onBlur={() => setTimeout(() => {
              setFocused(false);
              setShowSuggestions(false);
            }, 200)}
            placeholder="Search for foods (e.g., salmon, eggs, cheese)..."
            autoComplete="off"
          />
          {query && (
            <button
              className="clear-button-modern"
              onClick={() => {
                setQuery('');
                setResults([]);
                setShowSuggestions(false);
                inputRef.current?.focus();
              }}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
          {loading && (
            <div className="search-loading-indicator">
              <div className="spinner-small"></div>
            </div>
          )}
        </div>
      </div>

      {/* Suggestions when input is focused and no query or short query */}
      <AnimatePresence>
        {focused && showSuggestions && query.length < 2 && (
          <motion.div
            className="search-suggestions-modern"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            {recentSearches.length > 0 && (
              <div className="suggestions-section">
                <h4 className="suggestions-title">
                  <span className="suggestions-icon">🕒</span>
                  Recent Searches
                </h4>
                <div className="suggestions-list">
                  {recentSearches.map((search, idx) => (
                    <button
                      key={idx}
                      className="suggestion-item"
                      onClick={() => handleRecentSearchClick(search)}
                    >
                      <span className="suggestion-icon">🔍</span>
                      <span className="suggestion-text">{search}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
            {popularFoods.length > 0 && (
              <div className="suggestions-section">
                <h4 className="suggestions-title">
                  <span className="suggestions-icon">⭐</span>
                  Popular Foods
                </h4>
                <div className="suggestions-list">
                  {popularFoods.map((food, idx) => (
                    <button
                      key={food.id || idx}
                      className="suggestion-item"
                      onClick={() => handleSelect(food)}
                    >
                      <span className="suggestion-icon">🍽️</span>
                      <span className="suggestion-text">{food.name}</span>
                      {food.safetyStatus && (
                        <span 
                          className="suggestion-status"
                          style={{ 
                            color: getStatusConfig(food.safetyStatus).color,
                            background: getStatusConfig(food.safetyStatus).bg
                          }}
                        >
                          {getStatusConfig(food.safetyStatus).icon}
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Search Results */}
      <AnimatePresence>
        {focused && query.length >= 2 && results.length > 0 && (
          <motion.div
            className="search-results-container-modern"
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
          >
            <div className="results-header-modern">
              <span className="results-count-modern">
                {results.length} {results.length === 1 ? 'result' : 'results'} found
              </span>
            </div>
            <div className="results-grid-modern">
              {results.map((food) => (
                <SearchResultCard
                  key={food.id || food.name}
                  food={food}
                  onSelect={() => handleSelect(food)}
                  isChecking={checkingFoodId === (food.id || food.name)}
                  statusConfig={getStatusConfig(food.safetyStatus)}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* No Results */}
      <AnimatePresence>
        {focused && query.length >= 2 && !loading && results.length === 0 && (
          <motion.div
            className="no-results-container-modern"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="no-results-icon-large">🔍</div>
            <h4 className="no-results-title">No foods found</h4>
            <p className="no-results-text">
              We couldn't find any foods matching "{query}"
            </p>
            <p className="no-results-hint">
              Try searching with different keywords or check the spelling
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function SearchResultCard({ food, onSelect, isChecking, statusConfig }) {
  return (
    <motion.div
      className={`search-result-card-modern ${isChecking ? 'checking' : ''}`}
      onClick={onSelect}
      whileHover={{ scale: 1.02, y: -4 }}
      whileTap={{ scale: 0.98 }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <div className="result-card-header">
        <div className="result-name-section">
          <h4 className="result-name-modern">{food.name}</h4>
          {food.primaryNutrients && food.primaryNutrients.length > 0 && (
            <div className="nutrients-badges">
              {food.primaryNutrients.slice(0, 3).map((nutrient, idx) => (
                <span key={idx} className="nutrient-badge-small">
                  {nutrient}
                </span>
              ))}
            </div>
          )}
        </div>
        <div
          className="result-status-badge-modern"
          style={{
            background: statusConfig.bg,
            color: statusConfig.color,
            borderColor: statusConfig.color
          }}
        >
          <span className="status-icon-modern">{statusConfig.icon}</span>
          <span className="status-label-modern">{statusConfig.label}</span>
        </div>
      </div>

      {food.reason && (
        <p className="result-reason-modern">
          {food.reason.length > 120 
            ? `${food.reason.substring(0, 120)}...`
            : food.reason}
        </p>
      )}

      {food.alternative && (
        <div className="result-alternative">
          <span className="alternative-label">💡 Alternative:</span>
          <span className="alternative-text">{food.alternative}</span>
        </div>
      )}

      <div className="result-action-modern">
        {isChecking ? (
          <div className="checking-indicator">
            <div className="spinner-tiny"></div>
            <span>Checking safety...</span>
          </div>
        ) : (
          <span className="action-text-modern">
            Tap to check safety →
          </span>
        )}
      </div>
    </motion.div>
  );
}

export default FoodSearch;
